/**
 * components/editor/RichEditor.tsx
 * Full TipTap rich text editor with formatting toolbar.
 * Replaces the <textarea> in NoteEditor.tsx.
 * 
 * To use: import RichEditor from './RichEditor'
 * Then in NoteEditor replace:
 *   <textarea value={body} onChange={e => setBody(e.target.value)} .../>
 * With:
 *   <RichEditor content={body} onChange={(html, text) => { setBody(html); setPlain(text); }}/>
 */

import React, { useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Heading1, Heading2, Heading3,
  Code, Quote, Minus, Undo, Redo,
} from 'lucide-react';

const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A',
  ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570', border:'#E8E4DC',
  surface:'#FAFAF8', white:'#FFFFFF',
};
const font = { body:"'DM Sans',sans-serif" };

// ── Toolbar Button ─────────────────────────────────────────────────────────
const ToolBtn: React.FC<{
  onClick: () => void; active?: boolean; disabled?: boolean;
  title: string; children: React.ReactNode;
}> = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={onClick}
    style={{
      width:30, height:30, borderRadius:6, border:'none', cursor: disabled ? 'not-allowed' : 'pointer',
      background: active ? C.amber100 : 'transparent',
      color: active ? C.amber400 : disabled ? '#ccc' : C.muted,
      display:'flex', alignItems:'center', justifyContent:'center',
      transition:'background .1s, color .1s',
    }}
    onMouseEnter={e => { if (!disabled && !active) (e.currentTarget as HTMLButtonElement).style.background = C.surface; }}
    onMouseLeave={e => { if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
  >
    {children}
  </button>
);

// ── Divider ────────────────────────────────────────────────────────────────
const ToolDiv: React.FC = () => (
  <div style={{ width:1, height:18, background:C.border, margin:'0 4px', flexShrink:0 }}/>
);

// ── Main Editor ────────────────────────────────────────────────────────────
interface RichEditorProps {
  content: string;
  onChange: (html: string, plainText: string) => void;
  placeholder?: string;
  autofocus?: boolean;
}

const RichEditor: React.FC<RichEditorProps> = ({
  content, onChange,
  placeholder = 'Start writing… (Ctrl/⌘+B bold, Ctrl/⌘+I italic)',
  autofocus = true,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading:       { levels: [1, 2, 3] },
        bulletList:    { keepMarks: true },
        orderedList:   { keepMarks: true },
        codeBlock:     { languageClassPrefix: 'language-' },
      }),
      Placeholder.configure({ placeholder, emptyEditorClass:'is-editor-empty' }),
      Underline,
    ],
    content: content || '',
    autofocus: autofocus ? 'end' : false,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML(), editor.getText());
    },
    editorProps: {
      attributes: {
        style: [
          'outline:none',
          'min-height:300px',
          `font-family:${font.body}`,
          'font-size:15px',
          `color:${C.inkLight}`,
          'line-height:1.8',
          'padding:0',
        ].join(';'),
      },
    },
  }, []);  // Empty dep array — editor is stable

  const cmd = useCallback((fn: () => void) => {
    fn();
    editor?.commands.focus();
  }, [editor]);

  if (!editor) return null;

  return (
    <div style={{ display:'flex', flexDirection:'column', flex:1, overflow:'hidden' }}>
      {/* ── Toolbar ────────────────────────────────────────────────────── */}
      <div style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:2, padding:'8px 12px', borderBottom:`1px solid ${C.border}`, background:C.white, flexShrink:0 }}>

        {/* History */}
        <ToolBtn title="Undo (Ctrl+Z)" onClick={() => cmd(() => editor.chain().undo().run())} disabled={!editor.can().undo()}><Undo size={14}/></ToolBtn>
        <ToolBtn title="Redo (Ctrl+Y)" onClick={() => cmd(() => editor.chain().redo().run())} disabled={!editor.can().redo()}><Redo size={14}/></ToolBtn>
        <ToolDiv/>

        {/* Headings */}
        <ToolBtn title="Heading 1" active={editor.isActive('heading',{level:1})} onClick={() => cmd(() => editor.chain().toggleHeading({level:1}).run())}><Heading1 size={14}/></ToolBtn>
        <ToolBtn title="Heading 2" active={editor.isActive('heading',{level:2})} onClick={() => cmd(() => editor.chain().toggleHeading({level:2}).run())}><Heading2 size={14}/></ToolBtn>
        <ToolBtn title="Heading 3" active={editor.isActive('heading',{level:3})} onClick={() => cmd(() => editor.chain().toggleHeading({level:3}).run())}><Heading3 size={14}/></ToolBtn>
        <ToolDiv/>

        {/* Inline marks */}
        <ToolBtn title="Bold (Ctrl+B)" active={editor.isActive('bold')} onClick={() => cmd(() => editor.chain().toggleBold().run())}><Bold size={14}/></ToolBtn>
        <ToolBtn title="Italic (Ctrl+I)" active={editor.isActive('italic')} onClick={() => cmd(() => editor.chain().toggleItalic().run())}><Italic size={14}/></ToolBtn>
        <ToolBtn title="Underline (Ctrl+U)" active={editor.isActive('underline')} onClick={() => cmd(() => editor.chain().toggleUnderline().run())}><UnderlineIcon size={14}/></ToolBtn>
        <ToolBtn title="Strikethrough" active={editor.isActive('strike')} onClick={() => cmd(() => editor.chain().toggleStrike().run())}><Strikethrough size={14}/></ToolBtn>
        <ToolDiv/>

        {/* Lists */}
        <ToolBtn title="Bullet list" active={editor.isActive('bulletList')} onClick={() => cmd(() => editor.chain().toggleBulletList().run())}><List size={14}/></ToolBtn>
        <ToolBtn title="Numbered list" active={editor.isActive('orderedList')} onClick={() => cmd(() => editor.chain().toggleOrderedList().run())}><ListOrdered size={14}/></ToolBtn>
        <ToolDiv/>

        {/* Blocks */}
        <ToolBtn title="Code block" active={editor.isActive('codeBlock')} onClick={() => cmd(() => editor.chain().toggleCodeBlock().run())}><Code size={14}/></ToolBtn>
        <ToolBtn title="Blockquote" active={editor.isActive('blockquote')} onClick={() => cmd(() => editor.chain().toggleBlockquote().run())}><Quote size={14}/></ToolBtn>
        <ToolBtn title="Horizontal rule" onClick={() => cmd(() => editor.chain().setHorizontalRule().run())}><Minus size={14}/></ToolBtn>
      </div>

      {/* ── Editor content area ───────────────────────────────────────── */}
      <div style={{ flex:1, overflowY:'auto', padding:'20px 32px' }}>
        <style>{`
          .ProseMirror { outline: none; }
          .ProseMirror p { margin: 0 0 0.75em; }
          .ProseMirror h1 { font-family:'Sora',sans-serif; font-size:26px; font-weight:800; letter-spacing:-0.03em; margin:1.2em 0 0.4em; color:${C.ink}; }
          .ProseMirror h2 { font-family:'Sora',sans-serif; font-size:20px; font-weight:700; letter-spacing:-0.02em; margin:1em 0 0.35em; color:${C.ink}; }
          .ProseMirror h3 { font-family:'Sora',sans-serif; font-size:16px; font-weight:700; margin:0.8em 0 0.3em; color:${C.ink}; }
          .ProseMirror ul, .ProseMirror ol { padding-left:1.4em; margin:0.5em 0; }
          .ProseMirror li { margin-bottom:0.3em; }
          .ProseMirror code { background:${C.amber100}; color:${C.amber400}; padding:1px 6px; border-radius:4px; font-size:0.88em; font-family:monospace; }
          .ProseMirror pre { background:${C.ink}; color:#f8f8f2; padding:16px 20px; border-radius:10px; overflow-x:auto; margin:1em 0; }
          .ProseMirror pre code { background:none; color:inherit; padding:0; }
          .ProseMirror blockquote { border-left:3px solid ${C.amber300}; padding-left:16px; color:${C.muted}; margin:1em 0; font-style:italic; }
          .ProseMirror hr { border:none; border-top:1px solid ${C.border}; margin:1.5em 0; }
          .ProseMirror mark { background:${C.amber200}; color:${C.amber400}; border-radius:2px; padding:0 2px; }
          .ProseMirror p.is-editor-empty:first-child::before { content:attr(data-placeholder); color:${C.muted}; pointer-events:none; float:left; height:0; font-style:italic; }
          .ProseMirror strong { font-weight:700; color:${C.ink}; }
          .ProseMirror em { font-style:italic; }
          .ProseMirror s { text-decoration:line-through; }
        `}</style>
        <EditorContent editor={editor}/>
      </div>
    </div>
  );
};

export default RichEditor;
